<template>
  <div class="base64-converter">
    <!-- <header class="header">
      <h1><span class="highlight">Base64</span> 编码/解码工具</h1>
      <p class="subtitle">在文本和Base64格式之间快速转换</p>
    </header> -->

    <div class="converter-container">
      <!-- 编码区域 -->
      <div class="converter-section encode-section">
        <div class="section-header">
          <h2>
            <span class="icon">🔤</span>
            文本转Base64
          </h2>
          <div class="action-buttons">
            <button 
              @click="clearInput" 
              class="btn btn-secondary"
              :disabled="!textInput"
              title="清空输入">
              清空
            </button>
            <button 
              @click="copyToClipboard('encoded')" 
              class="btn btn-primary"
              :disabled="!encodedResult"
              title="复制Base64结果">
              复制结果
            </button>
          </div>
        </div>
        
        <div class="input-group">
          <label for="text-input">原始文本：</label>
          <textarea 
            id="text-input"
            v-model="textInput" 
            placeholder="输入要编码为Base64的文本..."
            class="input-textarea"
            rows="5"
            @input="encodeToBase64"
          ></textarea>
          <div class="input-info">
            <span class="char-count">字符数: {{ textInput.length }}</span>
            <span v-if="textInput" class="input-hint">实时编码中...</span>
          </div>
        </div>
        
        <div class="result-group">
          <label for="encoded-output">Base64编码结果：</label>
          <textarea 
            id="encoded-output"
            v-model="encodedResult" 
            placeholder="Base64编码结果将显示在这里..."
            class="output-textarea"
            rows="5"
            readonly
          ></textarea>
          <div v-if="encodedResult" class="result-info">
            <span class="char-count">编码长度: {{ encodedResult.length }}</span>
            <span class="success-badge">✓ 编码完成</span>
          </div>
        </div>
      </div>
      
      <!-- 中间转换按钮 -->
      <div class="swap-section">
        <button 
          @click="swapConversion" 
          class="btn-swap"
          title="切换编码/解码方向">
          <span class="swap-icon">⇄</span>
        </button>
        <p class="swap-hint">点击切换</p>
      </div>
      
      <!-- 解码区域 -->
      <div class="converter-section decode-section">
        <div class="section-header">
          <h2>
            <span class="icon">🔠</span>
            Base64转文本
          </h2>
          <div class="action-buttons">
            <button 
              @click="clearBase64Input" 
              class="btn btn-secondary"
              :disabled="!base64Input"
              title="清空输入">
              清空
            </button>
            <button 
              @click="copyToClipboard('decoded')" 
              class="btn btn-primary"
              :disabled="!decodedResult"
              title="复制解码结果">
              复制结果
            </button>
          </div>
        </div>
        
        <div class="input-group">
          <label for="base64-input">Base64编码：</label>
          <textarea 
            id="base64-input"
            v-model="base64Input" 
            placeholder="输入要解码的Base64字符串..."
            class="input-textarea"
            rows="5"
            @input="decodeFromBase64"
          ></textarea>
          <div class="input-info">
            <span class="char-count">字符数: {{ base64Input.length }}</span>
            <span v-if="base64Input && !decodeError" class="input-hint">实时解码中...</span>
            <span v-if="decodeError" class="error-hint">⚠ {{ decodeError }}</span>
          </div>
        </div>
        
        <div class="result-group">
          <label for="decoded-output">解码文本结果：</label>
          <textarea 
            id="decoded-output"
            v-model="decodedResult" 
            placeholder="解码后的文本将显示在这里..."
            class="output-textarea"
            rows="5"
            readonly
          ></textarea>
          <div v-if="decodedResult && !decodeError" class="result-info">
            <span class="char-count">字符数: {{ decodedResult.length }}</span>
            <span class="success-badge">✓ 解码完成</span>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 使用说明 -->
    <div class="instructions">
      <h3>📖 使用说明</h3>
      <ul>
        <li>在"文本转Base64"区域输入文本，会自动实时生成Base64编码</li>
        <li>在"Base64转文本"区域输入Base64字符串，会自动实时解码为原始文本</li>
        <li>点击<span class="highlight">⇄</span>按钮可以切换编码/解码方向</li>
        <li>使用"复制结果"按钮可以快速复制转换结果</li>
        <li>Base64编码常用于在HTTP环境中传输二进制数据</li>
      </ul>
    </div>
    
    <!-- 复制成功提示 -->
    <div v-if="showCopySuccess" class="copy-notification">
      <span class="copy-icon">✓</span> 已复制到剪贴板！
    </div>
  </div>
</template>

<script>
export default {
  name: 'Base64Converter',
  data() {
    return {
      textInput: '',
      base64Input: '',
      encodedResult: '',
      decodedResult: '',
      decodeError: '',
      showCopySuccess: false
    };
  },
  methods: {
    // Base64编码
    encodeToBase64() {
      if (!this.textInput) {
        this.encodedResult = '';
        return;
      }
      
      try {
        // 使用浏览器原生btoa函数进行Base64编码
        const encoded = btoa(unescape(encodeURIComponent(this.textInput)));
        this.encodedResult = encoded;
      } catch (error) {
        console.error('编码错误:', error);
        this.encodedResult = '编码错误：输入包含非ASCII字符';
      }
    },
    
    // Base64解码
    decodeFromBase64() {
      if (!this.base64Input) {
        this.decodedResult = '';
        this.decodeError = '';
        return;
      }
      
      // 清除Base64字符串中的空白字符
      const cleanBase64 = this.base64Input.replace(/\s/g, '');
      
      // 验证Base64格式
      if (!this.isValidBase64(cleanBase64)) {
        this.decodeError = '无效的Base64格式';
        this.decodedResult = '';
        return;
      }
      
      try {
        // 使用浏览器原生atob函数进行Base64解码
        const decoded = decodeURIComponent(escape(atob(cleanBase64)));
        this.decodedResult = decoded;
        this.decodeError = '';
      } catch (error) {
        console.error('解码错误:', error);
        this.decodeError = '解码错误：请检查Base64格式是否正确';
        this.decodedResult = '';
      }
    },
    
    // 验证Base64格式
    isValidBase64(str) {
      try {
        // Base64字符串长度应为4的倍数
        if (str.length % 4 !== 0) return false;
        
        // Base64应只包含特定字符
        const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
        return base64Regex.test(str);
      } catch (error) {
        return false;
      }
    },
    
    // 清空文本输入
    clearInput() {
      this.textInput = '';
      this.encodedResult = '';
    },
    
    // 清空Base64输入
    clearBase64Input() {
      this.base64Input = '';
      this.decodedResult = '';
      this.decodeError = '';
    },
    
    // 切换编码/解码方向
    swapConversion() {
      // 如果编码区域有内容，将其移动到解码区域
      if (this.textInput && this.encodedResult) {
        const tempText = this.textInput;
        this.textInput = this.base64Input;
        this.base64Input = this.encodedResult;
        
        // 重新计算
        this.encodeToBase64();
        this.decodeFromBase64();
        
        // 如果交换前解码区域有内容，将其移动到编码区域
        if (tempText) {
          setTimeout(() => {
            this.base64Input = tempText;
            this.decodeFromBase64();
          }, 10);
        }
      } 
      // 如果解码区域有内容，将其移动到编码区域
      else if (this.base64Input && this.decodedResult) {
        const tempBase64 = this.base64Input;
        this.base64Input = this.textInput;
        this.textInput = this.decodedResult;
        
        // 重新计算
        this.encodeToBase64();
        this.decodeFromBase64();
        
        // 如果交换前编码区域有内容，将其移动到解码区域
        if (tempBase64) {
          setTimeout(() => {
            this.textInput = tempBase64;
            this.encodeToBase64();
          }, 10);
        }
      }
    },
    
    // 复制到剪贴板
    copyToClipboard(type) {
      const textToCopy = type === 'encoded' ? this.encodedResult : this.decodedResult;
      
      if (!textToCopy) return;
      
      // 使用现代剪贴板API
      navigator.clipboard.writeText(textToCopy)
        .then(() => {
          this.showCopySuccess = true;
          setTimeout(() => {
            this.showCopySuccess = false;
          }, 2000);
        })
        .catch(err => {
          console.error('复制失败:', err);
          // 降级方案：使用旧版execCommand
          const textarea = document.createElement('textarea');
          textarea.value = textToCopy;
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand('copy');
          document.body.removeChild(textarea);
          
          this.showCopySuccess = true;
          setTimeout(() => {
            this.showCopySuccess = false;
          }, 2000);
        });
    }
  },
  watch: {
    // 监听输入变化，实现实时转换
    textInput() {
      this.encodeToBase64();
    },
    base64Input() {
      this.decodeFromBase64();
    }
  },
  mounted() {
    // 组件挂载时，可以设置一些示例文本
    this.textInput = 'Hello, Base64!';
    this.encodeToBase64();
  }
};
</script>

<style scoped>
.base64-converter {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  min-height: 100vh;
}

.header {
  text-align: center;
  margin-bottom: 2.5rem;
  padding: 1.5rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
}

.header h1 {
  font-size: 2.5rem;
  margin-bottom: 0.5rem;
  color: #2c3e50;
}

.highlight {
  color: #3498db;
  font-weight: 700;
}

.subtitle {
  color: #7f8c8d;
  font-size: 1.1rem;
}

.converter-container {
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;
  margin-bottom: 2.5rem;
}

.converter-section {
  flex: 1;
  min-width: 300px;
  background: white;
  border-radius: 12px;
  padding: 1.8rem;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s ease;
}

.converter-section:hover {
  transform: translateY(-5px);
}

.encode-section {
  border-top: 5px solid #3498db;
}

.decode-section {
  border-top: 5px solid #2ecc71;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #eee;
}

.section-header h2 {
  font-size: 1.5rem;
  color: #2c3e50;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.icon {
  font-size: 1.8rem;
}

.action-buttons {
  display: flex;
  gap: 0.8rem;
}

.btn {
  padding: 0.6rem 1.2rem;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.9rem;
}

.btn-primary {
  background-color: #3498db;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: #2980b9;
  transform: translateY(-2px);
}

.btn-primary:disabled {
  background-color: #bdc3c7;
  cursor: not-allowed;
}

.btn-secondary {
  background-color: #f8f9fa;
  color: #495057;
  border: 1px solid #dee2e6;
}

.btn-secondary:hover:not(:disabled) {
  background-color: #e9ecef;
}

.btn-secondary:disabled {
  color: #adb5bd;
  cursor: not-allowed;
}

.input-group, .result-group {
  margin-bottom: 1.5rem;
}

.input-group label, .result-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #2c3e50;
}

.input-textarea, .output-textarea {
  width: 100%;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  resize: vertical;
  transition: border-color 0.3s;
}

.input-textarea:focus {
  outline: none;
  border-color: #3498db;
}

.output-textarea {
  background-color: #f8f9fa;
  color: #495057;
}

.input-info, .result-info {
  display: flex;
  justify-content: space-between;
  margin-top: 0.5rem;
  font-size: 0.85rem;
}

.char-count {
  color: #7f8c8d;
}

.input-hint {
  color: #3498db;
  font-weight: 500;
}

.error-hint {
  color: #e74c3c;
  font-weight: 500;
}

.success-badge {
  color: #2ecc71;
  font-weight: 600;
}

.swap-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100px;
  align-self: center;
}

.btn-swap {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3498db, #2ecc71);
  border: none;
  color: white;
  font-size: 1.8rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 5px 15px rgba(52, 152, 219, 0.3);
}

.btn-swap:hover {
  transform: rotate(180deg) scale(1.1);
  box-shadow: 0 8px 20px rgba(52, 152, 219, 0.4);
}

.swap-hint {
  margin-top: 0.8rem;
  color: #7f8c8d;
  font-size: 0.9rem;
  font-weight: 500;
}

.instructions {
  background: white;
  border-radius: 12px;
  padding: 1.8rem;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
}

.instructions h3 {
  color: #2c3e50;
  margin-bottom: 1rem;
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.instructions ul {
  padding-left: 1.5rem;
  color: #555;
}

.instructions li {
  margin-bottom: 0.8rem;
  line-height: 1.5;
}

.copy-notification {
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  background: #2ecc71;
  color: white;
  padding: 1rem 1.5rem;
  border-radius: 8px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  gap: 0.8rem;
  animation: fadeInOut 2s ease;
  z-index: 1000;
}

.copy-icon {
  font-size: 1.2rem;
  font-weight: bold;
}

@keyframes fadeInOut {
  0%, 100% { opacity: 0; transform: translateY(20px); }
  10%, 90% { opacity: 1; transform: translateY(0); }
}

/* 响应式设计 */
@media (max-width: 768px) {
  .converter-container {
    flex-direction: column;
  }
  
  .swap-section {
    width: 100%;
    flex-direction: row;
    margin: 1rem 0;
  }
  
  .swap-hint {
    margin-top: 0;
    margin-left: 1rem;
  }
  
  .header h1 {
    font-size: 2rem;
  }
  
  .converter-section {
    min-width: 100%;
  }
}
</style>